/**
 * 금융기관 공동코드 — 대표코드·참가기관명 기준 (금융기관 공동코드 부여현황 등).
 * 비고(구명칭)는 사용하지 않음.
 *
 * 동일 참가기관명이 여러 대표코드인 경우(예: 농협은행 011·012, KB증권 218·226, 메리츠증권 268·287):
 * - 한글 정식 명칭으로 검색 시 표에서 앞에 나온 코드를 사용합니다.
 * - 다른 코드가 필요하면 숫자만 입력(예: 012, 226, 287).
 */

function norm(s: string): string {
  return s.trim().replace(/\s+/g, '').toLowerCase();
}

/**
 * [대표코드, [별칭…]]
 * 별칭: 참가기관명 전체 + 자주 쓰는 약어·구명칭(대구은행→031 아이엠뱅크 등)
 */
const BANK_CODE_ALIASES: Array<[string, string[]]> = [
  // --- 은행·금고·협동조합 등 ---
  ['002', ['산업은행', '산업', 'kdb']],
  ['003', ['기업은행', '기업', 'ibk']],
  ['004', ['국민은행', '국민', 'kb', 'kb국민', 'kb국민은행']],
  ['007', ['수협은행', '수협']],
  // 농협은행 011·012 동명 — 텍스트는 011 우선, 012는 숫자 직접 입력
  ['011', ['농협은행', '농협', 'nh', 'nh농협', 'nh은행']],
  ['012', []],
  ['020', ['우리은행', '우리', 'woori']],
  ['023', ['sc제일은행', 'sc제일', '제일은행', 'sc']],
  ['027', ['한국씨티은행', '씨티은행', '한국씨티', '씨티', 'citibank', 'citi']],
  ['031', ['아이엠뱅크', '아이엠', '대구은행', '대구', 'im뱅크']],
  ['032', ['부산은행', '부산']],
  ['034', ['광주은행', '광주']],
  ['035', ['제주은행', '제주']],
  ['037', ['전북은행', '전북']],
  ['039', ['경남은행', '경남']],
  ['045', ['새마을금고', '새마을', '새마을금고중앙회']],
  ['048', ['신용협동조합', '신협']],
  ['050', ['저축은행', '저축', 'sbi저축']],
  ['054', ['hsbc은행', 'hsbc']],
  ['057', ['제이피모간체이스', 'jp모간', '제이피모간']],
  ['059', ['엠유에프지은행', 'mufg', '엠유에프지']],
  ['060', ['boa은행', 'boa', '뱅크오브아메리카']],
  ['061', ['비엔피파리바은행', '비엔피', '파리바', 'bnp']],
  ['063', ['중국은행']],
  ['064', ['산림조합', '산림조합중앙회']],
  ['067', ['중국건설은행', '건설은행']],
  ['071', ['우체국', '우편']],
  ['081', ['keb하나', '하나은행', '하나', 'hana', 'keb']],
  ['088', ['신한은행', '신한', 'shinhan']],
  ['089', ['케이뱅크', 'k뱅크', '케이', 'kbank']],

  // --- 인터넷뱅크 등 ---
  ['090', ['카카오뱅크', '카카오', '카뱅', 'kakao']],
  ['092', ['토스뱅크', '토스', 'toss']],

  // --- 증권 ---
  ['209', ['유안타증권', '유안타']],
  // KB증권 218·226 동명 — 텍스트는 218 우선, 226은 숫자 직접 입력
  ['218', ['kb증권', 'KB증권']],
  ['224', ['bnk투자증권', 'BNK투자증권', 'bnk']],
  ['225', ['ibk투자증권', 'IBK투자증권', 'ibk투자']],
  ['226', []],
  ['227', ['다올투자증권', '다올']],
  ['238', ['미래에셋증권', '미래에셋', 'mirae']],
  ['240', ['삼성증권', '삼성']],
  ['243', ['한국투자증권', '한국투자', '한투']],
  ['263', ['현대차증권', '현대차']],
  ['264', ['키움증권', '키움']],
  ['265', ['엘에스증권', 'ls증권', 'LS증권']],
  ['266', ['sk증권', 'SK증권']],
  ['267', ['대신증권', '대신']],
  // 메리츠증권 268·287 동명 — 텍스트는 268 우선, 287은 숫자 직접 입력
  ['268', ['메리츠증권', '메리츠']],
  ['269', ['한화투자증권', '한화', '한화투자']],
  ['270', ['하나증권']],
  ['271', ['토스증권']],
  ['278', ['신한투자증권', '신한투자']],
  ['279', ['db금융투자', 'DB금융투자', 'db투자']],
  ['280', ['유진투자증권', '유진']],
  ['287', []],
  ['288', ['카카오페이증권', '카카오페이', '카페이증권']],
  ['289', ['nh투자증권', 'NH투자증권', '엔에이치투자']],
  ['290', ['부국증권', '부국']],
  ['291', ['신영증권', '신영']],
  ['292', ['케이프투자증권', '케이프']],
  ['294', ['우리투자증권', '우리투자']]
];

const aliasToCode: Record<string, string> = {};
for (const [code, names] of BANK_CODE_ALIASES) {
  aliasToCode[norm(code)] = code;
  for (const n of names) {
    aliasToCode[norm(n)] = code;
  }
}

/** 대표코드 → 표시용 한글 명칭(첫 별칭). 별칭 배열이 비어 있으면 미지정 */
const codeToPrimaryName: Record<string, string> = {};
for (const [code, names] of BANK_CODE_ALIASES) {
  if (names.length > 0) {
    codeToPrimaryName[code] = names[0];
  }
}

/**
 * A열 입력값을 금융기관 코드(3자리)로 맞춤.
 * - 1~3자리 숫자만 있으면 앞에 0을 붙임 (예: 4 → 004, 12 → 012)
 * - 은행·증권명 별칭이면 대표코드로 치환
 * - 매칭 실패 시 원문 유지
 */
export function resolveBankCodeInput(raw: string): string {
  const t = raw.trim();
  if (!t) return '';

  if (/^\d{1,3}$/.test(t)) {
    return t.padStart(3, '0');
  }

  const key = norm(t);
  const byAlias = aliasToCode[key];
  if (byAlias) return byAlias;

  return raw.trim();
}

/**
 * 저장된 코드·별칭을 은행/증권 표시명(한글 정식 명칭 우선)으로 변환.
 * 매핑 못 하면 3자리 코드 또는 원문 그대로.
 */
export function getBankPrimaryNameFromCode(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  const code = resolveBankCodeInput(t);
  if (code && codeToPrimaryName[code]) {
    return codeToPrimaryName[code];
  }
  if (code && /^\d{3}$/.test(code)) {
    return codeToPrimaryName[code] ?? code;
  }
  return t;
}
